// src/components/pdf/AdvancedStatsPage1.jsx
import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import styles from './styles';
import { SimplePieChart, CommentBox } from './charts/AdvancedCharts';

/**
 * Page 1 des statistiques avancées
 * Layout vertical : Donut points gagnés → Comparaison points gagnants → Donut points perdus → Comparaison fautes
 */
const AdvancedStatsPage1 = ({ match, playerName, playerCode, advancedStats, comments }) => {
  const playerIndex = playerCode === 'J1' ? 'j1' : 'j2';
  const pointsWon = advancedStats[playerIndex]?.pointsWon || [];
  const pointsLost = advancedStats[playerIndex]?.pointsLost || [];
  const comparison = advancedStats.comparison;
  
  // Composant de barre de comparaison (style app mobile avec couleurs joueurs)
  const ComparisonBar = ({ label, j1, j2 }) => {
    const total = j1 + j2;
    if (total === 0) return null;
    
    const j1Percent = (j1 / total) * 100;
    const j2Percent = (j2 / total) * 100;
    
    // Couleurs des joueurs
    const j1Color = '#1976D2'; // Bleu pour J1
    const j2Color = '#FF9800'; // Orange pour J2
    
    return (
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, marginBottom: 2 }}>{label}</Text>
        <View style={{ flexDirection: 'row', height: 16, backgroundColor: '#E0E0E0' }}>
          {/* Barre J1 (Vert) */}
          {j1 > 0 && (
            <View 
              style={{ 
                width: `${j1Percent}%`,
                backgroundColor: j1Color,
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {j1Percent > 15 && (
                <Text style={{ fontSize: 9, color: 'white', fontWeight: 'bold' }}>
                  {j1}
                </Text>
              )}
            </View>
          )}
          
          {/* Barre J2 (Rouge) */}
          {j2 > 0 && (
            <View 
              style={{ 
                width: `${j2Percent}%`,
                backgroundColor: j2Color,
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {j2Percent > 15 && (
                <Text style={{ fontSize: 9, color: 'white', fontWeight: 'bold' }}>
                  {j2}
                </Text>
              )}
            </View>
          )}
          
          {/* Valeurs à l'extérieur si barres trop petites */}
          {j1 > 0 && j1Percent <= 15 && (
            <Text 
              style={{ 
                position: 'absolute',
                left: -15,
                fontSize: 8,
                color: j1Color,
                fontWeight: 'bold'
              }}
            >
              {j1}
            </Text>
          )}
          
          {j2 > 0 && j2Percent <= 15 && (
            <Text 
              style={{ 
                position: 'absolute',
                right: -15,
                fontSize: 8,
                color: j2Color,
                fontWeight: 'bold'
              }}
            >
              {j2}
            </Text>
          )}
        </View>
      </View>
    );
  };
  
  // Préparer les données de comparaison points gagnants
  const comparisonPointsData = [
    { label: 'Points au service', j1: comparison.j1.pointsService, j2: comparison.j2.pointsService },
    { label: 'Points en retour', j1: comparison.j1.pointsRetour, j2: comparison.j2.pointsRetour },
    { label: 'Points en coup droit', j1: comparison.j1.pointsCoupDroit, j2: comparison.j2.pointsCoupDroit },
    { label: 'Points en revers', j1: comparison.j1.pointsRevers, j2: comparison.j2.pointsRevers },
    { label: 'Points à la volée', j1: comparison.j1.pointsVolee, j2: comparison.j2.pointsVolee }
  ];
  
  // Préparer les données de comparaison fautes
  const comparisonFautesData = [
    { label: 'Fautes au service', j1: comparison.j1.fautesService, j2: comparison.j2.fautesService },
    { label: 'Fautes en retour', j1: comparison.j1.fautesRetour, j2: comparison.j2.fautesRetour },
    { label: 'Fautes en coup droit', j1: comparison.j1.fautesCoupDroit, j2: comparison.j2.fautesCoupDroit },
    { label: 'Fautes en revers', j1: comparison.j1.fautesRevers, j2: comparison.j2.fautesRevers },
    { label: 'Fautes à la volée', j1: comparison.j1.fautesVolee, j2: comparison.j2.fautesVolee }
  ];
  
  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>Statistiques Avancées - Répartition des Points</Text>
      
      {/* Espacement après le titre */}
      <View style={{ height: 10 }} />
      
      {/* Layout en 2 colonnes */}
      <View style={{ flexDirection: 'row', gap: 15, marginTop: 10 }}>
        {/* COLONNE GAUCHE */}
        <View style={{ flex: 1 }}>
          {/* Donut points gagnés */}
          <SimplePieChart
            data={pointsWon}
            title="Répartition des points gagnés"
            width={250}
            height={200}
          />
        </View>
        
        {/* COLONNE DROITE */}
        <View style={{ flex: 1 }}>
          {/* Donut points perdus */}
          <SimplePieChart
            data={pointsLost}
            title="Répartition des points perdus"
            width={250}
            height={200}
          />
        </View>
      </View>
      
      {/* Commentaire commun aux 2 donuts - pleine largeur, APRÈS les donuts */}
      {comments?.bloc1 && (
        <View style={{ marginTop: -10 }}>
          <CommentBox comment={comments.bloc1} />
        </View>
      )}
      
      {/* Espacement entre les blocs de graphes */}
      <View style={{ height: 30 }} />
      
      {/* Layout 2 colonnes pour les graphes de comparaison */}
      <View style={{ flexDirection: 'row', gap: 15 }}>
        {/* COLONNE GAUCHE */}
        <View style={{ flex: 1 }}>
          {/* Titre section */}
          <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 5, textAlign: 'center' }}>
            Comparaison des points gagnants
          </Text>
          
          {/* Échelle supérieure (sous le titre) */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>0%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>20%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>40%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>60%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>80%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>100%</Text>
          </View>
          
          {/* Barres de comparaison points */}
          {comparisonPointsData.map((item, index) => (
            <ComparisonBar
              key={index}
              label={item.label}
              j1={item.j1}
              j2={item.j2}
            />
          ))}
          
          {/* Échelle inférieure (sous les barres) */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>100%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>80%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>60%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>40%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>20%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>0%</Text>
          </View>
        </View>
        
        {/* COLONNE DROITE */}
        <View style={{ flex: 1 }}>
          {/* Titre section */}
          <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 5, textAlign: 'center' }}>
            Comparaison des fautes
          </Text>
          
          {/* Échelle supérieure (sous le titre) */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>0%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>20%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>40%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>60%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>80%</Text>
            <Text style={{ fontSize: 7, color: '#1976D2' }}>100%</Text>
          </View>
          
          {/* Barres de comparaison fautes */}
          {comparisonFautesData.map((item, index) => (
            <ComparisonBar
              key={index}
              label={item.label}
              j1={item.j1}
              j2={item.j2}
            />
          ))}
          
          {/* Échelle inférieure inversée */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>100%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>80%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>60%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>40%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>20%</Text>
            <Text style={{ fontSize: 7, color: '#FF9800' }}>0%</Text>
          </View>
        </View>
      </View>
      
      {/* Commentaire commun aux 2 graphes de comparaison - pleine largeur, APRÈS les comparaisons */}
      {comments?.bloc2 && (
        <View style={{ marginTop: 10 }}>
          <CommentBox comment={comments.bloc2} />
        </View>
      )}
    </Page>
  );
};

export default AdvancedStatsPage1;